import BaseModal from 'components/BaseModal'
import { FlashNoticeVariant } from 'components/FlashNotice'
import { showFlashNotice } from 'components/FlashNotice/duck/actions'
import { RootState } from 'duck/store'
import React from 'react'
import { connect } from 'react-redux'
import { compose } from 'recompose'
import SectionLoading from 'scenes/Dashboard/components/SectionLoading'
import {
  apiGetPersonAsAdmin,
  apiGetPersonEducationsAsAdmin,
  apiGetPersonRolesAsAdmin,
} from 'scenes/Dashboard/data/people/api'
import { Education, newPerson, Person, PersonData, Position } from 'scenes/Dashboard/data/people/types'

import { withStyles, WithStyles } from '@mui/styles'

import { closePersonForm, createPerson, updatePerson } from '../../duck/actions'
import { getPeopleManagerState } from '../../duck/selectors'
import { PeopleManagerState } from '../../duck/types'
import PersonForm from './PersonForm.component'
import styles from './PersonFormModal.styles'
import PeopleModalHeader from './PersonModalHeader.component'

class PersonFormModal extends React.PureComponent<
  PersonFormModalProps & WithStyles<typeof styles>,
  PersonFormModalState
> {
  constructor(props: PersonFormModalProps & WithStyles<typeof styles>) {
    super(props)
    this.state = {
      isLoading: true,
      isInEditMode: true,
    }
    this.fetchPerson = this.fetchPerson.bind(this)
    this.openEditionMode = this.openEditionMode.bind(this)
    this.closeEditionMode = this.closeEditionMode.bind(this)
    this.onSubmit = this.onSubmit.bind(this)
  }

  // eslint-disable-next-line camelcase
  UNSAFE_componentWillMount() {
    const { personId, personFormOpen } = this.props
    if (personFormOpen) {
      this.fetchPerson(personId)
    }
  }

  componentDidUpdate(prevProps: PersonFormModalProps) {
    if (
      prevProps.personId !== this.props.personId ||
      (prevProps.personFormOpen !== this.props.personFormOpen && this.props.personFormOpen)
    ) {
      this.fetchPerson(this.props.personId)
    }
  }

  async fetchPerson(personId?: number | null) {
    this.setState({
      isLoading: true,
    })
    const { onClose, onPersonError } = this.props
    let person
    if (!personId) {
      person = newPerson() as PersonData
    } else {
      try {
        const personData = (await apiGetPersonAsAdmin(personId)) as PersonData
        const roles = (await apiGetPersonRolesAsAdmin(personId)) as Position[]
        const educations = (await apiGetPersonEducationsAsAdmin(personId)) as Education[]
        person = {
          ...personData,
          roles,
          educations,
        }
      } catch (e) {
        onPersonError({
          message: 'Error opening person.',
          autoHide: false,
          variant: FlashNoticeVariant.error,
        })
        onClose()
      }
    }
    this.setState({
      person,
      isLoading: false,
    })
  }

  openEditionMode() {
    this.setState({
      isInEditMode: true,
    })
  }

  closeEditionMode() {
    this.setState({
      isInEditMode: false,
    })
  }

  onSubmit(person: Person) {
    if (person.id) {
      this.props.onUpdatePerson(person)
    } else {
      this.props.onNewPerson(person)
    }
  }

  render() {
    const { classes, personFormOpen = false, onClose } = this.props
    const { isLoading, person, isInEditMode } = this.state

    const modalTitle = <PeopleModalHeader person={person} />

    return (
      <BaseModal open={personFormOpen} title={modalTitle} onClose={onClose}>
        {isLoading && <SectionLoading className={classes.loadingContainer} />}
        {person && (
          <PersonForm
            person={person as Person}
            isInEditMode={isInEditMode}
            onSubmit={this.onSubmit}
            onCancel={onClose}
            onEdit={this.openEditionMode}
          />
        )}
      </BaseModal>
    )
  }
}

export type PersonFormModalProps = {
  personFormOpen?: boolean
  personId?: number | null
  onPersonError: (config: any) => void
  onUpdatePerson: (person: Person) => void
  onNewPerson: (person: Person) => void
  onClose: () => void
}

type PersonFormModalState = {
  isLoading: boolean
  person?: PersonData
  isInEditMode: boolean
}

const mapStateToProps = (state: RootState): Partial<PersonFormModalProps> => {
  const managerState: PeopleManagerState = getPeopleManagerState(state)
  return {
    personFormOpen: managerState.personFormOpen,
    personId: managerState.selectedPersonId,
  }
}

const mapDispatchToProps = {
  onClose: closePersonForm,
  onUpdatePerson: updatePerson,
  onNewPerson: createPerson,
  onPersonError: showFlashNotice,
}

export default compose<PersonFormModalProps & WithStyles<typeof styles>, {}>(
  connect(mapStateToProps, mapDispatchToProps),
  withStyles(styles)
)(PersonFormModal)
